import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import PageHeader from '../components/PageHeader'
import SectionIntro from '../components/SectionIntro'
import Reveal from '../components/Reveal'
import Icon from '../components/Icons'
import { Marquee } from '../components/Motion'
import { CallToAction } from './Home'
import { services, serviceDetails, values } from '../data/site'

const process = [
  {
    title: 'Consult',
    body: 'We start with your actual constraints — budget, timelines, and the systems already in place.',
  },
  {
    title: 'Scope & quote',
    body: 'A clear, costed proposal, structured to meet direct procurement or competitive tender requirements.',
  },
  {
    title: 'Deliver',
    body: 'Build, supply, and install with genuine licensing and quality-assured hardware.',
  },
  {
    title: 'Support',
    body: 'After-sales support and advisory that continues past handover.',
  },
]

const Services = () => {
  const [openService, setOpenService] = useState(services[0].slug)

  return (
  <main>
    <PageHeader
      index="02"
      label="What we provide"
      lines={[
        'Services built',
        'around how you',
        <>
          actually <span className="accent-italic">buy</span>
        </>,
      ]}
      description="Eight practice areas spanning technology, supply, and creative work — delivered directly or through competitive tender."
      crumb="Services"
    />

    {/* ---------------- Service index ---------------- */}
    <section className="section">
      <div className="container">
        <div className="border-y border-ink-950/10">
          {services.map((service, i) => {
            const isOpen = openService === service.slug
            return (
              <Reveal key={service.slug} delay={i * 50}>
                <article className="border-b border-ink-950/10 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setOpenService(isOpen ? '' : service.slug)}
                    className="grid w-full items-center gap-x-8 gap-y-3 py-8 text-left md:grid-cols-[4rem_1fr_3rem]"
                    aria-expanded={isOpen}
                  >
                    <span className="index-num">{String(i + 1).padStart(2, '0')}</span>
                    <span>
                      <span className="display-3 block">{service.title}</span>
                      <span className="mt-2 block text-sm text-ink-500">{service.blurb}</span>
                    </span>
                    <span className={`flex h-10 w-10 items-center justify-center rounded-full ring-1 ring-inset ring-ink-950/15 transition-transform duration-300 ${isOpen ? 'rotate-45 bg-ink-950 text-paper-100' : 'text-ink-950'}`}>
                      <Icon name="arrow" className="h-4 w-4 rotate-90" />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="grid gap-8 pb-9 md:grid-cols-[4rem_1fr_3rem]">
                      <div />
                      <div>
                        <p className="label">Included services</p>
                        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                          {serviceDetails[service.slug].map((detail) => (
                            <li key={detail} className="flex gap-3 text-sm leading-relaxed text-ink-500">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                              {detail}
                            </li>
                          ))}
                        </ul>
                        <Link to="/contact" className="link-arrow group mt-7">
                          <span className="border-b border-ink-950/30 pb-1 transition-colors group-hover:border-ink-950">Request this service</span>
                          <Icon name="arrow" className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  )}
                </article>
              </Reveal>
            )
          })}
        </div>

        <Reveal delay={140} className="mt-14">
          <Link to="/contact" className="btn btn-outline">
            Request a quote
            <Icon name="arrow" className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>

    {/* ---------------- Process ---------------- */}
    <section className="relative overflow-hidden bg-ink-950 bg-glow grain">
      <div className="container relative section">
        <SectionIntro
          index="03"
          label="How we work"
          tone="light"
          lines={[
            'A predictable path',
            <>
              from brief to <span className="accent-italic">handover</span>
            </>,
          ]}
          description="The same four steps whether the engagement is a direct order or a competitive tender."
        />

        <div className="mt-20 grid gap-px bg-paper-100/12 md:grid-cols-2 lg:grid-cols-4">
          {process.map((step, i) => (
            <Reveal key={step.title} delay={i * 90}>
              <div className="h-full bg-ink-950 p-8 lg:p-9">
                <span className="index-num text-accent-400">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-6 font-display text-2xl font-medium tracking-tighter2 text-paper-100">
                  {step.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-paper-100/55">{step.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* ---------------- Values ---------------- */}
    <section className="border-b border-ink-950/10 bg-paper-100 py-10">
      <Marquee speed="fast">
        {values.map((value) => (
          <span key={value} className="flex items-center">
            <span className="whitespace-nowrap px-8 font-display text-xl font-medium tracking-tighter2 text-ink-950/70">
              {value}
            </span>
            <span className="h-1 w-1 shrink-0 rounded-full bg-ochre" />
          </span>
        ))}
      </Marquee>
    </section>

    <CallToAction />
  </main>
  )
}

export default Services
